import { useEffect, useRef } from "react";
import { useAppStatusStore } from "@/shared/stores/appStatusStore";

// Poll slowly under normal conditions (deploys are rare); once a deploy is
// detected, switch to the fast interval so the reload-on-finish stays prompt.
const IDLE_INTERVAL_MS = 30000;
const ACTIVE_INTERVAL_MS = 5000;
const FLAG_URL = "/maintenance.json";
const DEFAULT_MESSAGE = "Deployment in progress — please wait…";

interface MaintenanceFlag {
  active?: boolean;
  message?: string;
}

/**
 * Polls a static flag file written by the deploy script. Works while the API
 * is restarting because nginx serves it directly from the frontend's web root.
 *
 * The deploy script writes {"active":true} before work and {"active":false}
 * when finished, so the file is always present (a 404 is treated as inactive).
 */
export const useMaintenancePoll = () => {
  const setMaintenance = useAppStatusStore((s) => s.setMaintenance);
  const clearMaintenance = useAppStatusStore((s) => s.clearMaintenance);
  // Track whether we were in maintenance so we can reload when it clears.
  const wasActive = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let timer: number;

    const schedule = (ms: number) => {
      if (cancelled) return;
      timer = window.setTimeout(check, ms);
    };

    const check = async () => {
      // Don't poll while the tab is in the background.
      if (document.visibilityState === "hidden") {
        schedule(IDLE_INTERVAL_MS);
        return;
      }

      try {
        const res = await fetch(FLAG_URL, { cache: "no-store" });
        if (cancelled) return;

        // Any non-200 (missing file, SPA fallback) means no deployment.
        const data: MaintenanceFlag = res.ok ? await res.json() : { active: false };
        if (cancelled) return;

        if (data.active === true) {
          wasActive.current = true;
          setMaintenance(data.message || DEFAULT_MESSAGE);
          schedule(ACTIVE_INTERVAL_MS);
        } else {
          if (wasActive.current) {
            window.location.reload();
            return;
          }
          clearMaintenance();
          schedule(IDLE_INTERVAL_MS);
        }
      } catch {
        // Network blip — leave current state as is and retry later.
        schedule(IDLE_INTERVAL_MS);
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        window.clearTimeout(timer);
        check();
      }
    };

    check();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [setMaintenance, clearMaintenance]);
};
